import React, { useState, useEffect, useMemo } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton, Pagination, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import SearchInput from "../../../../components/SearchInput";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import empty from "../../../../assets/empty.png";
import AttendanceFilterDrawer from "./AttendanceFilterDrawer.js";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";

const TaskAttendanceHistory = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");
  
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    startDate: "",
    endDate: "",
    group_by_date: false,
  });
  const pageSize = 10;

  const fetchAttendanceHistory = async (dateFilters = {}) => {
    try {
      setLoading(true);
      
      // Fetch all records; paginate client-side after search.
      let url = `${BASE_URL}/task-management/worker/tasks/attendance-history?page=1&page_size=50000`;
      
      // Add status filter (supports comma-separated values)
      if (dateFilters.status) {
        url += `&status=${dateFilters.status}`;
      }
      
      // API expects plain YYYY-MM-DD (e.g. dateFrom=2026-05-21&dateTo=2026-05-21)
      if (dateFilters.startDate) {
        url += `&dateFrom=${dateFilters.startDate}`;
      }
      if (dateFilters.endDate) {
        url += `&dateTo=${dateFilters.endDate}`;
      }
      
      // Add group_by_date if enabled
      if (dateFilters.group_by_date) {
        url += `&group_by_date=true`;
      }

      const res = await fetch(url, {
        method: "GET",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!data.error) {
        const history = data.data?.tasks || [];
        setAttendanceHistory(history);
      } else {
        console.error("❌ Error fetching attendance history:", data.message);
        setAttendanceHistory([]);
      }
    } catch (err) {
      console.error("❌ Error fetching attendance history:", err);
      setAttendanceHistory([]);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAttendanceHistory(filters);
    }
  }, [filters, token]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterApplied(!!(newFilters.status || newFilters.startDate || newFilters.endDate || newFilters.group_by_date));
    setPage(1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
    return localDateTime === "—" ? "-" : localDateTime;
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return "--";
    const localTime = convertUTCToLocalDateTime(dateString, {
      locale: "en-GB",
      formatOptions: {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    });
    return localTime === "—" ? "--" : localTime;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "--";
    const localDate = convertUTCToLocalDateTime(dateString, {
      locale: "en-GB",
      formatOptions: {
        day: "2-digit",
        month: "short",
        year: "numeric",
      },
    });
    return localDate === "—" ? "--" : localDate;
  };

  const formatDelay = (delayMinutes) => {
    if (!delayMinutes || delayMinutes === 0) return "--";
    
    const hours = Math.floor(delayMinutes / 60);
    const minutes = delayMinutes % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes} minutes`;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "#8F00FF"; // Default purple
    const statusLower = status.toLowerCase();
    if (statusLower === "on_time") {
      return "#00C48C"; // Green
    } else if (statusLower === "late") {
      return "#FF974A"; // Orange
    } else if (statusLower === "not_started" || statusLower === "not_ended") {
      return "#8F00FF"; // Purple for pending
    }
    return "#8F00FF"; // Default purple
  };

  const getStatusLabel = (item) => {
    if (!item.attendance) return "-";
    
    const { start_status, end_status } = item.attendance;
    
    // Scenario: Task not started
    if (start_status === "not_started") {
      return t("taskAttendance.notStarted") || "Not Started";
    }
    
    // Scenario #1 & #2: Task started but not ended
    if (start_status && end_status === "not_ended") {
      if (start_status === "on_time") {
        return t("taskAttendance.startTaskOnTime") || "Start Task / On Time Start Task";
      } else if (start_status === "late") {
        return t("taskAttendance.startTaskLate") || "Start Task / Late Start Task";
      }
    }
    
    // Scenario #3 & #4: Task completed - show end status
    if (end_status === "on_time") {
      return t("taskAttendance.endTaskOnTime") || "End Task / On Time End Task";
    } else if (end_status === "late") {
      return t("taskAttendance.endTaskLate") || "End Task / Late End Task";
    }
    
    // Fallback: If we have start status but no end status info
    if (start_status === "on_time") {
      return t("taskAttendance.startTaskOnTime") || "Start Task / On Time Start Task";
    } else if (start_status === "late") {
      return t("taskAttendance.startTaskLate") || "Start Task / Late Start Task";
    }
    
    return "-";
  };

  // Get combined status label showing both start and end if available
  const getCombinedStatusLabel = (item) => {
    if (!item.attendance) return "-";
    
    const { start_status, end_status } = item.attendance;
    const parts = [];
    
    // Add start status
    if (start_status === "on_time") {
      parts.push(t("taskAttendance.startTaskOnTime") || "Start Task / On Time Start Task");
    } else if (start_status === "late") {
      parts.push(t("taskAttendance.startTaskLate") || "Start Task / Late Start Task");
    } else if (start_status === "not_started") {
      return t("taskAttendance.notStarted") || "Not Started";
    }
    
    // Add end status if task is completed
    if (end_status === "on_time") {
      parts.push(t("taskAttendance.endTaskOnTime") || "End Task / On Time End Task");
    } else if (end_status === "late") {
      parts.push(t("taskAttendance.endTaskLate") || "End Task / Late End Task");
    } else if (end_status === "not_ended" && start_status !== "not_started") {
      // Task started but not ended - only show start status
      return parts[0] || "-";
    }
    
    return parts.length > 0 ? parts.join(" • ") : "-";
  };

  const getStatusColorForItem = (item) => {
    if (!item.attendance) return "#8F00FF";
    
    const { start_status, end_status } = item.attendance;
    
    // Priority: end_status > start_status
    if (end_status && end_status !== "not_ended") {
      return getStatusColor(end_status);
    }
    
    if (start_status && start_status !== "not_started") {
      return getStatusColor(start_status);
    }
    
    return getStatusColor(start_status || end_status);
  };

  const HeaderStyles = {
    color: "#44546F",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const DataStyles = {
    color: "#172B4D",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // Helper functions for status labels
  const getStartStatusLabelHelper = (item) => {
    if (!item.attendance) return "";
    const { start_status } = item.attendance;
    if (start_status === "on_time") return t("taskAttendance.onTime") || "On Time";
    if (start_status === "late") return t("taskAttendance.late") || "Late";
    if (start_status === "not_started") return t("taskAttendance.notStarted") || "Not Started";
    return "";
  };

  const getEndStatusLabelHelper = (item) => {
    if (!item.attendance) return "";
    const { end_status } = item.attendance;
    if (end_status === "on_time") return t("taskAttendance.onTime") || "On Time";
    if (end_status === "late") return t("taskAttendance.late") || "Late";
    if (end_status === "not_ended") return t("taskAttendance.notEnded") || "Not Ended";
    return "";
  };

  const filteredHistory = useMemo(() => {
    return attendanceHistory.filter((item) => {
      if (!searchTerm) return true;

      const searchableText = [
        item.title || "",
        item.task_id?.toString() || "",
        formatDateOnly(item.scheduled?.start_at || item.task_date),
        formatTimeOnly(item.scheduled?.start_at),
        formatTimeOnly(item.actual?.started_at),
        formatTimeOnly(item.scheduled?.end_at),
        formatTimeOnly(item.actual?.ended_at),
        getStartStatusLabelHelper(item),
        getEndStatusLabelHelper(item),
        formatDelay(item.attendance?.start_delay_minutes),
        formatDelay(item.attendance?.end_delay_minutes),
      ].join(" ").toLowerCase();

      return searchableText.includes(searchTerm.toLowerCase());
    });
  }, [attendanceHistory, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedHistory = filteredHistory.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  if (isInitialLoad && loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          py: 2,
          mt: 3,
          border: "2px solid #091E4224",
        }}
      >
        {/* Header with Search & Filter */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: isMobile ? "wrap" : "nowrap",
            pb: 2,
            px: 2,
            borderBottom: "2px solid #091E4224",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontWeight: 600,
                fontSize: "18px",
                color: "#003149",
                textAlign: "left",
              }}
            >
              {t("task_attendance_history")}
            </CustomText>
            {loading && !isInitialLoad && (
              <CircularProgress size={18} sx={{}} />
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <SearchInput
              placeholder={t("projectManagement.searchTasks")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {isFilterApplied ? (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 34,
                  height: 34,
                  borderRadius: 1,
                  bgcolor: "#1976d2",
                }}
              >
                <FilterAltIcon sx={{ color: "white", fontSize: 20 }} />
                <IconButton
                  size="small"
                  onClick={() => {
                    setFilters({ status: "", startDate: "", endDate: "", group_by_date: false });
                    setPage(1);
                    setIsFilterApplied(false);
                  }}
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    bgcolor: "white",
                    width: 18,
                    height: 18,
                    border: "1px solid #C4C4C4",
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 14, color: "#333" }} />
                </IconButton>
              </Box>
            ) : (
              <IconButton
                onClick={() => setOpenFilterDrawer(true)}
                sx={{
                  border: "1px solid #C4C4C4",
                  borderRadius: 1,
                  width: 34,
                  height: 34,
                }}
              >
                <FilterAltIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead
              sx={{
                borderTop: "2px solid #091E4224",
                borderBottom: "2px solid #091E4224",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <TableRow>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.taskId") || "Task ID"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.taskName") || "Task Name"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.date") || "Date"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.scheduledStart") || "Scheduled Start"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.actualStart") || "Actual Start"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.startStatus") || "Start Status"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.startDelay") || "Start Delay"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.scheduledEnd") || "Scheduled End"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.actualEnd") || "Actual End"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.endStatus") || "End Status"}</TableCell>
                <TableCell sx={{ ...HeaderStyles }}>{t("taskAttendance.endDelay") || "End Delay"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 5,
                      }}
                    >
                      <img
                        src={empty}
                        alt="No Data"
                        style={{ width: 200, marginBottom: 16 }}
                      />
                      <CustomText
                        sx={{
                          ...textStyles.h1,
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#003149",
                        }}
                      >
                        {t("empty.no_user")}
                      </CustomText>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedHistory.map((item, index) => {
                  const { start_status, end_status, start_delay_minutes, end_delay_minutes } = item.attendance || {};
                  
                  // Get scheduled and actual times
                  const scheduledStart = item.scheduled?.start_at;
                  const actualStart = item.actual?.started_at;
                  const scheduledEnd = item.scheduled?.end_at;
                  const actualEnd = item.actual?.ended_at;
                  
                  // Prefer scheduled UTC instant so date column matches converted times
                  const taskDate = scheduledStart || item.task_date;
                  
                  // Get status labels
                  const startStatusLabel = start_status === "on_time" 
                    ? (t("taskAttendance.onTime") || "On Time")
                    : start_status === "late"
                    ? (t("taskAttendance.late") || "Late")
                    : start_status === "not_started"
                    ? (t("taskAttendance.notStarted") || "Not Started")
                    : "--";
                  
                  const endStatusLabel = end_status === "on_time"
                    ? (t("taskAttendance.onTime") || "On Time")
                    : end_status === "late"
                    ? (t("taskAttendance.late") || "Late")
                    : end_status === "not_ended"
                    ? (t("taskAttendance.notEnded") || "Not Ended")
                    : "--";
                  
                  // Get status colors
                  const startStatusColor = start_status === "on_time" 
                    ? "#00C48C"
                    : start_status === "late"
                    ? "#FF974A"
                    : start_status === "not_started"
                    ? "#8F00FF"
                    : "#8F00FF";
                  
                  const endStatusColor = end_status === "on_time"
                    ? "#00C48C"
                    : end_status === "late"
                    ? "#FF974A"
                    : end_status === "not_ended"
                    ? "#FFDC60"
                    : "#8F00FF";
                  
                  return (
                    <TableRow key={item.task_id || index} hover>
                      <TableCell sx={{ ...DataStyles }}>
                        {item.task_id || "-"}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        <TruncatedCell text={item.title || "-"} maxLength={25} />
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatDateOnly(taskDate)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatTimeOnly(scheduledStart)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatTimeOnly(actualStart)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: `${startStatusColor}20`,
                            color: startStatusColor,
                            fontSize: "12px",
                            fontWeight: 500,
                            width: "fit-content",
                          }}
                        >
                          {startStatusLabel}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatDelay(start_delay_minutes)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatTimeOnly(scheduledEnd)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatTimeOnly(actualEnd)}
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: `${endStatusColor}20`,
                            color: endStatusColor,
                            fontSize: "12px",
                            fontWeight: 500,
                            width: "fit-content",
                          }}
                        >
                          {endStatusLabel}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...DataStyles }}>
                        {formatDelay(end_delay_minutes)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Box>

      {/* Filter Drawer */}
      <AttendanceFilterDrawer
        open={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        filters={filters}
        setFilters={setFilters}
        onApplyFilters={handleApplyFilters}
      />
    </Box>
  );
};

export default TaskAttendanceHistory;
